import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import Row from '../components/Row';
import VideoModal from '../components/VideoModal';
import { tmdb, titleOf, IMG } from '../lib/tmdb';
import { Play, X } from 'lucide-react';

export default function Browse() {
  const [hero, setHero] = useState(null);
  const [rows, setRows] = useState({});
  const [modal, setModal] = useState({ open: false, key: null, title: '' });
  const [detail, setDetail] = useState(null);
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    (async () => {
      const [trending, originals, topRated, action, comedy, horror, romance, docs] =
        await Promise.all([
          tmdb.trending(),
          tmdb.netflixOriginals(),
          tmdb.topRated(),
          tmdb.actionMovies(),
          tmdb.comedyMovies(),
          tmdb.horrorMovies(),
          tmdb.romanceMovies(),
          tmdb.documentaries(),
        ]);
      const trendList = trending?.results || [];
      const withBackdrop = trendList.filter((i) => i.backdrop_path);
      setHero(withBackdrop[Math.floor(Math.random() * Math.min(5, withBackdrop.length))]);
      setRows({
        'Trending Now': trendList,
        'Netflix Originals': originals?.results || [],
        'Top Rated': topRated?.results || [],
        'Action & Adventure': action?.results || [],
        'Comedies': comedy?.results || [],
        'Scary Movies': horror?.results || [],
        'Romance': romance?.results || [],
        'Documentaries': docs?.results || [],
      });
    })();
  }, []);

  const playTrailer = useCallback(async (item) => {
    setModal({ open: true, key: null, title: titleOf(item) });
    const key = await tmdb.videos(item);
    setModal({ open: true, key, title: titleOf(item) });
  }, []);

  const openInfo = useCallback((item) => setDetail(item), []);

  const handleSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    const data = await tmdb.search(q.trim());
    setSearchResults((data?.results || []).filter((i) => i.poster_path || i.backdrop_path));
  }, []);

  return (
    <div className="min-h-screen bg-[#141414] pb-16">
      <Navbar onSearch={handleSearch} />

      {searchResults ? (
        <div className="pt-24 px-4 md:px-12">
          <h2 className="text-xl md:text-2xl font-bold mb-4">Search Results</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 md:gap-3">
            {searchResults.map((item) => (
              <img
                key={item.id}
                src={`https://image.tmdb.org/t/p/w500${item.poster_path || item.backdrop_path}`}
                alt={titleOf(item)}
                onClick={() => playTrailer(item)}
                className="w-full h-auto rounded cursor-pointer hover:scale-105 transition-transform"
              />
            ))}
            {searchResults.length === 0 && (
              <p className="text-white/60 col-span-full">No results found.</p>
            )}
          </div>
        </div>
      ) : (
        <>
          <Hero item={hero} onPlay={playTrailer} onInfo={openInfo} />
          <div className="relative z-20 -mt-16 md:-mt-32">
            {Object.entries(rows).map(([title, items], idx) => (
              <Row
                key={title}
                title={title}
                items={items}
                onPlay={playTrailer}
                large={idx === 1}
              />
            ))}
          </div>
        </>
      )}

      <VideoModal
        open={modal.open}
        youtubeKey={modal.key}
        title={modal.title}
        onClose={() => setModal({ open: false, key: null, title: '' })}
      />

      {detail && (
        <div
          className="fixed inset-0 z-[55] bg-black/70 flex items-start justify-center overflow-y-auto py-10 animate-fade-in"
          onClick={() => setDetail(null)}
        >
          <div
            className="relative w-full max-w-3xl bg-[#181818] rounded-lg overflow-hidden mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64 md:h-96">
              <img
                src={`${IMG}${detail.backdrop_path}`}
                alt={titleOf(detail)}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#181818] to-transparent" />
              <button
                onClick={() => setDetail(null)}
                className="absolute top-3 right-3 bg-[#181818] rounded-full p-2"
              >
                <X size={20} />
              </button>
              <div className="absolute bottom-6 left-6">
                <h2 className="text-2xl md:text-4xl font-black mb-4">{titleOf(detail)}</h2>
                <button
                  onClick={() => { setDetail(null); playTrailer(detail); }}
                  className="flex items-center gap-2 bg-white text-black font-semibold px-6 py-2 rounded hover:bg-white/80"
                >
                  <Play size={20} fill="black" /> Play
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 text-sm mb-3">
                <span className="text-green-500 font-semibold">
                  {Math.round((detail.vote_average || 0) * 10)}% Match
                </span>
                <span className="text-white/60">
                  {(detail.release_date || detail.first_air_date || '').slice(0, 4)}
                </span>
                <span className="border border-white/40 px-1.5 text-xs">HD</span>
              </div>
              <p className="text-sm md:text-base text-white/85 leading-relaxed">
                {detail.overview || 'No description available.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}